import request from "../api/httpClient.js";
import getEndpoint from "../utils/getEndpoint.js";

const inFlightScriptRequests = new Map();
const latestScriptCache = new Map();
const LATEST_SCRIPT_CACHE_TTL_MS = 30000;

const withInFlightDedup = async (key, factory) => {
  if (inFlightScriptRequests.has(key)) {
    return inFlightScriptRequests.get(key);
  }

  const promise = factory().finally(() => {
    inFlightScriptRequests.delete(key);
  });

  inFlightScriptRequests.set(key, promise);
  return promise;
};

const invalidateScriptCaches = (animationId) => {
  if (!animationId) return;
  latestScriptCache.delete(animationId);
  for (const key of inFlightScriptRequests.keys()) {
    if (key.startsWith(`list:${animationId}:`)) {
      inFlightScriptRequests.delete(key);
    }
  }
};

export const createScript = async (animationId, scriptText) => {
  const script = await request(getEndpoint("studio", "createScript", animationId), {
    body: { scriptText },
  });
  invalidateScriptCaches(animationId);
  if (script?.data?.script) {
    latestScriptCache.set(animationId, {
      data: script.data.script,
      expiresAt: Date.now() + LATEST_SCRIPT_CACHE_TTL_MS,
    });
  }
  return script.data;
};

export const saveScriptDraft = async (animationId, scriptText, options = {}) => {
  const script = await request(
    getEndpoint("studio", "saveScriptDraft", animationId),
    {
      body: { scriptText },
      signal: options.signal,
    },
  );
  invalidateScriptCaches(animationId);
  if (script?.data) {
    latestScriptCache.set(animationId, {
      data: script.data,
      expiresAt: Date.now() + LATEST_SCRIPT_CACHE_TTL_MS,
    });
  }
  return script.data;
};

export const previewScript = async (
  animationId,
  scriptText,
  { signal, summaryMode = "none" } = {},
) => {
  const animation = await request(
    getEndpoint("studio", "previewScript", animationId),
    {
      body: { scriptText, summaryMode },
      signal,
    },
  );
  return animation.data;
};

export const getScript = async (animationId) => {
  const cached = latestScriptCache.get(animationId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  const script = await withInFlightDedup(`latest:${animationId}`, () =>
    request(getEndpoint("studio", "getScript", animationId)),
  );
  if (script?.data) {
    latestScriptCache.set(animationId, {
      data: script.data,
      expiresAt: Date.now() + LATEST_SCRIPT_CACHE_TTL_MS,
    });
  }
  return script.data;
};

export const getScripts = async (animationId, query) => {
  const queryKey = JSON.stringify(query || {});
  const scripts = await withInFlightDedup(
    `list:${animationId}:${queryKey}`,
    () =>
      request(getEndpoint("studio", "getScripts", animationId), {
        query,
      }),
  );
  return scripts.data;
};
